import { type ApiErrorBody } from '@re/shared';
import { compressImage } from '@re/form-ui';
import { tr } from '../lib/i18n';

const ORIGIN = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const BASE = `${ORIGIN}/api/v1/org`;

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: NonNullable<ApiErrorBody['error']['details']> = [],
  ) {
    super(message);
  }
  /** field → message map for forms */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const d of this.details) if (d.field) out[d.field] = d.message ?? this.message;
    return out;
  }
}

// ---------------------------------------------------------------------------
// Access token lives in memory only; the refresh token is an httpOnly cookie.
// ---------------------------------------------------------------------------
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;
export const setAccessToken = (t: string | null) => {
  accessToken = t;
};
export const setSessionLostHandler = (fn: () => void) => {
  onSessionLost = fn;
};

let refreshing: Promise<boolean> | null = null;
export function refreshSession(): Promise<boolean> {
  refreshing ??= fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' })
    .then(async (r) => {
      if (!r.ok) return false;
      const j = (await r.json()) as { accessToken: string };
      accessToken = j.accessToken;
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

type Query = Record<string, string | number | boolean | undefined | null | (string | number)[]>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

function qs(query?: Query): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const res = await fetch(`${BASE}${path}${qs(opts.query)}`, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    signal: opts.signal,
    headers: {
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...opts.headers,
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    if (await refreshSession()) return api<T>(path, opts, true);
    accessToken = null;
    onSessionLost?.();
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = (data as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, e?.code ?? 'HTTP_' + res.status, e?.message ?? tr(`خطأ ${res.status}`, `Error ${res.status}`), e?.details ?? []);
  }
  return data as T;
}

/** presign → PUT to storage → fileId */
export async function uploadFile(original: File, presignPath = '/files/presign'): Promise<string> {
  const file = await compressImage(original);
  const p = await api<{ fileId: string; uploadUrl: string; headers: Record<string, string> }>(presignPath, {
    method: 'POST',
    body: { fileName: file.name, contentType: file.type, sizeBytes: file.size },
  });
  const put = await fetch(p.uploadUrl, { method: 'PUT', headers: p.headers, body: file });
  if (!put.ok) throw new Error(tr('فشل رفع الملف إلى التخزين', 'Failed to upload file to storage'));
  return p.fileId;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** Load cursor pages so Data Grid can filter/sort on the loaded set. */
export async function fetchAllPages<T>(path: string, query?: Query): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 20; i++) {
    const page = await api<Page<T>>(path, { query: { ...query, cursor, limit: 100 } });
    items.push(...page.items);
    if (!page.nextCursor || page.nextCursor === cursor) return items;
    cursor = page.nextCursor;
  }
  return items;
}
